import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import SafeImg from '../components/SafeImg';
import {
  GALLERY_FILTER_TABS,
  galleryCategoryMatchesFilter,
  normalizeGalleryCategory,
} from '../config/galleryCategories';

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    apiService.gallery.getAll()
      .then(res => setImages(res.data))
      .catch(() => {
        setImages([
          { id: 1, title: 'Annual Function', description: 'Cultural performances', category: 'Events', imageUrl: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800' },
          { id: 2, title: 'Sports Day', description: 'Athletic events', category: 'Sports', imageUrl: 'https://images.unsplash.com/photo-1461896836934- voices60dcc7c?w=800' },
          { id: 3, title: 'Science Lab', description: 'Modern facilities', category: 'Infrastructure', imageUrl: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800' },
          { id: 4, title: 'Classroom', description: 'Smart classrooms', category: 'Infrastructure', imageUrl: 'https://images.unsplash.com/photo-1588072432836-e10032774350?w=800' },
          { id: 5, title: 'Music Class', description: 'Creative learning', category: 'Activities', imageUrl: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800' },
          { id: 6, title: 'Computer Lab', description: 'Digital learning', category: 'Infrastructure', imageUrl: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800' }
        ]);
      });
  }, []);

  const categories = GALLERY_FILTER_TABS;
  const filteredImages = images.filter((img) => galleryCategoryMatchesFilter(img.category, filter));

  return (
    <>
      <SEO 
        title="Photo Gallery | Campus Life & Events | Vidya Vatika Panipat"
        description="Explore our vibrant campus through photos of events, activities, infrastructure and student life at Vidya Vatika World School."
        keywords="school gallery Panipat, campus photos, school events images, Vidya Vatika pictures, CBSE school gallery Haryana"
        canonicalUrl="/gallery"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold font-display mb-4">Photo Gallery</h1>
            <p className="text-xl text-blue-100">Capturing moments of excellence</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  filter === cat 
                    ? 'bg-blue-900 text-white' 
                    : 'bg-white text-gray-700 hover:bg-blue-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((img, index) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedImage(img)}
                className="bg-white rounded-2xl shadow-lg overflow-hidden cursor-pointer hover:shadow-2xl transition-all transform hover:scale-105"
              >
                <SafeImg src={img.imageUrl} alt={img.title} className="w-full h-64 object-cover" />
                <div className="p-4">
                  <span className="inline-block bg-accent text-white text-xs px-3 py-1 rounded-full mb-2">
                    {normalizeGalleryCategory(img.category)}
                  </span>
                  <h3 className="text-lg font-bold text-blue-900">{img.title}</h3>
                  <p className="text-sm text-gray-600">{img.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Lightbox Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div className="max-w-5xl w-full">
              <SafeImg 
                src={selectedImage.imageUrl} 
                alt={selectedImage.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="text-white mt-4 text-center">
                <h3 className="text-2xl font-bold mb-2">{selectedImage.title}</h3>
                <p className="text-lg">{selectedImage.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GalleryPage;
